import { useMemo } from 'react';

import { isPartialPolicyPayload, TypedPolicy } from '../../../types';

import {
  NETWORK_SECURITY_FORM_INITIAL_VALUES,
  NETWORK_SECURITY_INITIAL_VALUES,
  KubernetesNetworkSecurityFormValues,
  NetworkSecurityPolicyPayload,
  KubernetesNetworkSecurityTypeSpecificData,
} from './types';

/**
 * Type guard to check if data is KubernetesNetworkSecurityPolicyPayload
 */
function isKubernetesNetworkSecurityPolicyPayload(
  data: unknown
): data is NetworkSecurityPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'ingressRules' in data &&
    'ingressRules' in data
  );
}
/**
 * Deserializes a Kubernetes Network Security policy payload into form values.
 * Used by both edit mode (loading existing policy) and template mode (applying template data).
 *
 * @param data - The policy payload data to deserialize
 * @param defaults - Default values to use when payload fields are missing
 * @returns Type-specific form values
 */
function deserializePayload(
  data: Partial<NetworkSecurityPolicyPayload>,
  defaults: KubernetesNetworkSecurityTypeSpecificData
): KubernetesNetworkSecurityTypeSpecificData {
  let namespaces = defaults.namespaces;
  if (data.ingressRules && data.ingressRules.length !== 0) {
    const rule = data.ingressRules[0];
    if (rule.appliesTo) {
      namespaces = rule.appliesTo.namespaces.map((namespace) => {
        return { namespace: namespace };
      });
    }
  } else if (data.egressRules && data.egressRules.length !== 0) {
    const rule = data.egressRules[0];
    if (rule.appliesTo) {
      namespaces = rule.appliesTo.namespaces.map((namespace) => {
        return { namespace: namespace };
      });
    }
  }

  return {
    namespaces: namespaces,
    ingressRules: data.ingressRules ?? defaults.ingressRules,
    egressRules: data.egressRules ?? defaults.egressRules,
  };
}

/**
 * Hook to get initial values for Kubernetes Network Security Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @param templateData - Optional template data to pre-fill form (for create from template)
 * @returns Complete form initial values
 */
export function useKubernetesNetworkSecurityInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): KubernetesNetworkSecurityFormValues {
  return useMemo(() => {
    const defaults = NETWORK_SECURITY_INITIAL_VALUES;

    // Template mode: apply template data over defaults
    if (
      !policy &&
      templateData &&
      isPartialPolicyPayload<NetworkSecurityPolicyPayload>(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }

    // Create mode: use defaults
    if (!policy) {
      return NETWORK_SECURITY_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesNetworkSecurityPolicyPayload(data)) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        ...defaults,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}
